import { BrandLogo } from '@components/Brand/BrandLogo';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiBadge, NoddiBasicCard, NoddiButton } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { DepartmentOrganizationName, ErrorPage, ImpersonatingBanner, log, useAuthContext } from 'noddi-web';
import { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useOnImpersonated } from '../../hooks/useOnImpersonated';
import { getUserGroupProfileTypeLabel, useUserGroupSelection } from '../../hooks/useUserGroupSelection';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import {
  getOnlyUserGroupToSelect,
  getUserGroupCardDescription,
  isUserGroupOrganizationContext
} from '../../utils/userGroupContext';

export const UserGroupSelection = () => {
  const navigate = useNavigate();
  const { logout, impersonatedUser, currentUserGroupId } = useAuthContext();
  const { selectUserGroup, hasMultipleSelectableUserGroups, selectableUserGroups, selectedUserGroup, userData } =
    useUserGroupSelection();
  const onImpersonated = useOnImpersonated();
  const { currentBrand } = useCurrentBrand();

  // A single selectable group is the user's only context, but nothing marks it as the current
  // group when it is an organization (login only defaults to the personal group). Select it here
  // so the homepage has a group to load — otherwise it renders blank.
  const autoSelectGroup = getOnlyUserGroupToSelect(selectableUserGroups, currentUserGroupId);

  useEffect(() => {
    if (autoSelectGroup) {
      // The state that looped in CUSTOMER-APP-15F: the group flagged selected, the session id
      // missing. Handled by selecting it; reported so a cause other than a refused storage write
      // (which AuthProvider reports itself) shows up.
      if (autoSelectGroup.id === selectedUserGroup?.id) {
        log({ value: 'Only selectable group flagged selected without a session group id', severity: 'warning' });
      }
      selectUserGroup(autoSelectGroup.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectUserGroup, selectedUserGroup omitted: select once per auto-selected group id
  }, [autoSelectGroup?.id]);

  if (!userData) {
    return null;
  }

  // Checked before the single-group redirect: with no selectable group the homepage has nothing
  // to load and sends the user straight back here. Log out is the only way on from this state.
  if (selectableUserGroups.length === 0) {
    return (
      <ErrorPage
        title={t`No Users Available`}
        description={t`There are no users associated with your account.`}
        // eslint-disable-next-line noddi/no-banned-ui-terms -- log message, not UI text: keeps the backend term for searchability
        logInput={{ severity: 'error', value: 'No user groups found for user' }}
        // oxlint-disable-next-line typescript/no-misused-promises -- async onButtonClick runs logout(); the button discards the returned promise, so a rejection is unhandled unless the body catches
        buttonProps={{ buttonText: t`Log out`, onButtonClick: logout }}
      />
    );
  }

  if (autoSelectGroup) {
    return null;
  }

  if (!hasMultipleSelectableUserGroups) {
    // replace: this hop is automatic, so Back must not land on it and bounce forward again
    return <Navigate replace to={customerRoutes.homepage.getPath()} />;
  }

  const handleUserGroupSelect = (userGroup: { id: number; name: string }) => {
    selectUserGroup(userGroup.id);
    navigate(customerRoutes.homepage.getPath());
  };

  return (
    <div className='min-h-screen bg-primary-background-gradient'>
      {!!impersonatedUser && <ImpersonatingBanner onImpersonated={onImpersonated} defaultBrandDomain={currentBrand} />}
      <div className='relative top-2 mx-auto mb-5 max-w-screen-lg rounded-full bg-white/60 px-4 py-2 max-lg:mx-4 sm:top-4 sm:mb-4 sm:px-5 sm:py-1.5 md:px-10 md:py-4'>
        <div className='flex w-full items-center gap-4 sm:gap-5'>
          <BrandLogo />

          <div className='ml-auto flex gap-2 sm:gap-3'>
            <NoddiButton
              variant='outline'
              size='small'
              className='rounded-full border-none text-xs sm:text-sm'
              startIcon='LogOut'
              // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs logout(); React discards the returned promise, so a rejection is unhandled unless the body catches
              onClick={logout}
            >
              <Trans>Log out</Trans>
            </NoddiButton>
          </div>
        </div>
      </div>

      <div className='mx-auto max-w-5xl px-4 py-6 sm:px-5 sm:py-12 lg:px-12 lg:py-16'>
        <div className='flex flex-col items-center gap-12 sm:gap-20'>
          <div className='flex flex-col items-center gap-5 text-center sm:gap-6'>
            <div className='flex size-16 items-center justify-center rounded-full sm:size-20'>
              <NoddiIcon name='Users' size='large' className='text-primary sm:hidden' />
              <NoddiIcon name='Users' size={40} className='hidden text-primary sm:block' />
            </div>
            {/* eslint-disable-next-line noddi/enforce-spacing-scale -- space-y stack not migrated to flex-col gap yet; swapping changes child margin layout */}
            <div className='space-y-3 sm:space-y-4'>
              <h1 className='text-2xl font-bold tracking-tight text-primary sm:text-4xl lg:text-5xl xl:text-6xl'>
                <Trans>Select user</Trans>
              </h1>
              <p className='max-w-3xl px-4 text-base sm:text-lg lg:text-xl'>
                <Trans>
                  Your account is linked to multiple users. Please choose the user you'd like to manage. To make
                  bookings for a company car, choose the organization.
                </Trans>
              </p>
            </div>
          </div>

          <div className='grid w-full grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3'>
            {selectableUserGroups.map((userGroup) => {
              const isOrgContext = isUserGroupOrganizationContext(userGroup);
              const onlyDepartment = userGroup.departments.length === 1 ? userGroup.departments[0] : undefined;
              return (
                <NoddiBasicCard
                  key={userGroup.id}
                  size='small'
                  className='group relative min-h-40 cursor-pointer overflow-hidden border-2 text-black10 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl sm:min-h-45'
                  onClick={() => handleUserGroupSelect(userGroup)}
                >
                  <div className='flex h-full flex-col justify-between gap-4 p-4 sm:gap-5 sm:p-5'>
                    <div className='flex flex-col items-center gap-3 text-center sm:gap-4'>
                      <div className='flex size-14 items-center justify-center rounded-2xl bg-gray-100 transition-all duration-300 group-hover:scale-110 sm:size-16'>
                        <NoddiIcon
                          name={isOrgContext ? 'Building2' : 'CircleUserRound'}
                          size={28}
                          className='sm:hidden'
                        />
                        <NoddiIcon
                          name={isOrgContext ? 'Building2' : 'CircleUserRound'}
                          size='large'
                          className='hidden sm:block'
                        />
                      </div>
                      {onlyDepartment ? (
                        <DepartmentOrganizationName
                          departmentName={onlyDepartment.name}
                          organizationName={onlyDepartment.organizationName}
                          className='items-center text-center text-lg leading-tight sm:text-xl'
                        />
                      ) : (
                        <h3 className='text-lg leading-tight font-semibold sm:text-xl'>{userGroup.name}</h3>
                      )}
                      <NoddiBadge variant={isOrgContext ? 'info' : 'outline'}>
                        {getUserGroupProfileTypeLabel(userGroup)}
                      </NoddiBadge>
                      <p className='text-sm text-muted-foreground'>{getUserGroupCardDescription(userGroup)}</p>
                    </div>

                    <div className='flex justify-center'>
                      <div className='transition-all duration-300 group-hover:translate-x-1 group-hover:text-primary'>
                        <NoddiIcon name='ArrowRight' size='small' className='sm:hidden' />
                        <NoddiIcon name='ArrowRight' size='xMedium' className='hidden sm:block' />
                      </div>
                    </div>
                  </div>

                  <div className='absolute inset-0 bg-primary/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100' />
                </NoddiBasicCard>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
